import axios from "axios";
import { globalErrorHandler } from "../utils/ErrorHandler";
import { encryptMsalValue, decryptMsalValue } from "../utils/msalStorage";

// Base API URLs
const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const AGENT_BASE_URL = process.env.REACT_APP_AGENT_API_BASE_URL;
const CSRF_TOKEN_ENDPOINT = "/api/Auth/csrf-token";

// CSRF Token Management
class CSRFTokenManager {
  constructor() {
    this.token = null;
    this.isRefreshing = false;
    this.refreshPromise = null;
  }

  async getToken(authHeaders) {
    if (this.token) {
      return this.token;
    }

    if (this.isRefreshing) {
      return this.refreshPromise;
    }

    return this.refreshToken(authHeaders);
  }

  async refreshToken(authHeaders) {
    this.isRefreshing = true;

    this.refreshPromise = axios
      .get(`${BASE_URL}${CSRF_TOKEN_ENDPOINT}`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
      })
      .then((response) => {
        this.token = response.data.token || response.data.csrfToken;
        console.log("✅ CSRF Token fetched successfully");
        this.isRefreshing = false;
        this.refreshPromise = null;
        return this.token;
      })
      .catch((error) => {
        this.isRefreshing = false;
        this.refreshPromise = null;
        console.error("❌ CSRF token fetch failed");
        throw error;
      });

    return this.refreshPromise;
  }

  clearToken() {
    this.token = null;
  }
}

const csrfManager = new CSRFTokenManager();

// Auto-logout timer reset function (set from Layout component)
let globalResetTimer = null;

/**
 * Set the auto-logout reset function to be called on API requests
 * @param {Function} resetFn - Function to reset the auto-logout timer
 */
export const setAutoLogoutReset = (resetFn) => {
  globalResetTimer = resetFn;
};

const safeJsonParse = (value) => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    console.error("Failed to parse MSAL storage entry:", error);
    return null;
  }
};

// Helper function to get auth token from sessionStorage
const getAuthToken = () => {
  try {
        // Get all keys from sessionStorage
   const keys = Object.keys(sessionStorage);
    
    // Look for msal token
    const mslKeys = keys.filter(k => k.includes("msal") && k.includes("access_token"));
    
    if (mslKeys.length > 0) {
      const tokenData = decryptMsalValue(sessionStorage.getItem(mslKeys[0]));
      if (tokenData) {
        const parsed = safeJsonParse(tokenData);
        return parsed?.secret || parsed?.access_token || null;
      }
    }

    // Fallback: try the original method
    const idTokenKey = decryptMsalValue(
      sessionStorage.getItem(
      `msal.token.keys.${process.env.REACT_APP_MSAL_CLIENT_ID}`
      )
    );

    if (!idTokenKey) {
      console.warn("⚠️ No MSAL token key found");
      return null;
    }

    const parsedIdTokenKey = safeJsonParse(idTokenKey);
    if (!parsedIdTokenKey?.idToken?.length) {
      console.warn("⚠️ Malformed MSAL token key payload");
      return null;
    }

    const token = decryptMsalValue(
      sessionStorage.getItem(`${parsedIdTokenKey.idToken[0]}`)
    );

    if (!token) {
      console.warn("⚠️ No token found in sessionStorage");
      return null;
    }

    const parsedToken = safeJsonParse(token);
    const tokenAuth = encryptMsalValue(parsedToken?.secret || null)
    return tokenAuth;//parsedToken?.secret || null;
  } catch (error) {
    console.error("❌ Error retrieving auth token:", error);
    return null;
  }
};

// Create API instances
const axiosClient = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// Handle unauthorized response
const handleUnauthorized = () => {
  console.warn("🚨 Unauthorized - Clearing session and redirecting");
  globalErrorHandler.handleUnauthorized();
};

// Request interceptor - FIRST: Add Auth Token
const addAuthTokenInterceptor = (client) => {
  client.interceptors.request.use(
    (config) => {
     const token = getAuthToken();
      
      if (token) {
        config.headers.Authorization = `${token}`;
        console.log(`✅ Auth header added for ${config.method.toUpperCase()}`);
      } else {
        console.warn(`⚠️ No auth token available for ${config.method.toUpperCase()}`);
      }
      
      // Reset auto-logout timer on API calls (user activity)
      if (globalResetTimer && typeof globalResetTimer === 'function') {
        globalResetTimer();
      }
      
      return config;
    },
    (error) => Promise.reject(error)
  );
};

// CSRF Token Interceptor - SECOND: Add CSRF token to state-changing requests
const addCSRFTokenInterceptor = (client) => {
  client.interceptors.request.use(
    async (config) => {
      const stateChangingMethods = ["post", "put", "delete", "patch"];

      if (stateChangingMethods.includes(config.method?.toLowerCase())) {
        try {
          const token = getAuthToken();
          
          if (!token) {
            console.error("❌ No auth token available for CSRF fetch");
            return Promise.reject(new Error("No authentication token"));
          }

          const csrfToken = await csrfManager.getToken({
            Authorization: `${token}`,
          });
          
          config.headers["X-CSRF-Token"] = csrfToken;
          console.log(`✅ CSRF header added for ${config.method.toUpperCase()}`);
        } catch (error) {
          console.error(`❌ Failed to add CSRF token for ${config.method.toUpperCase()}`);
          return Promise.reject(error);
        }
      }

      return config;
    },
    (error) => Promise.reject(error)
  );
};

// Response interceptor - Handle errors with global error handler
const addResponseInterceptor = (client) => {
  client.interceptors.response.use(
    (response) => {
      console.log(`✅ Success: ${response.config.method.toUpperCase()}`);
      return response;
    },
    (error) => {
      const status = error.response?.status;

      // Use global error handler for safe error logging
      globalErrorHandler.handleError(error, 'API Response');

      if (status === 401) {
        handleUnauthorized();
      }

      if (status === 403) {
        const shouldClearCSRF = globalErrorHandler.handleForbidden(error);
        if (shouldClearCSRF) {
          csrfManager.clearToken();
        }
      }

      return Promise.reject(error);
    }
  );
};

// Create agent API instance
const agentApiInstance = axios.create({
  baseURL: AGENT_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth interceptor to agent API
addAuthTokenInterceptor(agentApiInstance);

// Simple error handler for agent API
agentApiInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      handleUnauthorized();
    }

    if (error.response?.status === 404) {
      throw new Error(
        "Query endpoint not found. Please check if the service is running."
      );
    } else if (error.response?.status === 400) {
      throw new Error(
        "Invalid query parameters. Please check your input."
      );
    } else if (error.response?.status >= 500) {
      throw new Error(
        "Server error occurred while processing your query. Please try again later."
      );
    } else if (error.code === "ECONNABORTED") {
      throw new Error(
        "Query request timed out. The service might be experiencing high load."
      );
    } else if (!error.response && error.request) {
      throw new Error(
        "No response received from the query service. Please check your network connection."
      );
    }

    throw error;
  }
);

// Apply interceptors in CORRECT ORDER
addAuthTokenInterceptor(axiosClient);
addCSRFTokenInterceptor(axiosClient);
addResponseInterceptor(axiosClient);
addResponseInterceptor(agentApiInstance);

// Export clients and CSRF manager
export { axiosClient as default, agentApiInstance, csrfManager };